import React from 'react';
import PropTypes from 'prop-types';

import {cloneDeep} from 'lodash';
import DraggableList from 'common/components/draggable-list';
import Category from './components/category';
import {
  getSelectedChoicesSort,
  getSelectionsSort,
  unselectedChoicesSort,
} from './helpers';
import './index.scss';

/*
 * FormOrderedSelectMultiple
 *
 */
export default class FormOrderedSelectMultiple extends React.Component {
  static propTypes = {
    /*
      `data` provides ordering and selections:
      [
        ['provider_x', ['upstream_a', 'upstream_c']],
        ['provider_z', ['upstream_a', 'upstream_b']],
        ['provider_y', ['upstream_b', 'upstream_c']],
      ]
    */
    data: PropTypes.arrayOf(
      PropTypes.arrayOf(
        PropTypes.oneOfType([
          PropTypes.string, // selected category
          PropTypes.arrayOf(PropTypes.any), // selected children
        ]),
      ),
    ),

    /*
     `field.widget.choices` don't have to be ordered,
     just to follow this structure:
      [
        [['provider_y', 'Provider Y'], [
          ['upstream_a', 'Upstream A'],
          ['upstream_b', 'Upstream B'],
          ['upstream_c', 'Upstream C'],
        ]],
        [['provider_x', 'Provider X'], [
          ['upstream_a', 'Upstream A'],
          ['upstream_b', 'Upstream B'],
          ['upstream_c', 'Upstream C'],
        ]],
        [['provider_z', 'Provider Z'], [
          ['upstream_a', 'Upstream A'],
          ['upstream_b', 'Upstream B'],
          ['upstream_c', 'Upstream C'],
        ]],
      ]
    */
    field: PropTypes.shape({
      widget: PropTypes.shape({
        choices: PropTypes.arrayOf(
          PropTypes.arrayOf(
            PropTypes.oneOfType([
              PropTypes.arrayOf(PropTypes.string),
              PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.any)),
            ]),
          ),
        ),
      }),
    }).isRequired,

    handleChange: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      data: props.data,
    };
  }

  static getDerivedStateFromProps(props, state) {
    if (JSON.stringify(props.data) !== JSON.stringify(state.prevData)) {
      return {
        data: props.data,
      };
    }
    return null;
  }

  getOrderedChoices() {
    const {choices} = this.props.field.widget;
    const {data} = this.state || [];
    const orderedCategorySelections = data.map((category) => category[0]);

    const selectedCategoryChoices = [];
    const unselectedCategoryChoices = [];
    for (const choice of choices) {
      const category = choice[0][0];
      if (orderedCategorySelections.includes(category)) {
        selectedCategoryChoices.push(choice);
      } else {
        unselectedCategoryChoices.push(choice);
      }
    }

    selectedCategoryChoices.sort(
      getSelectedChoicesSort(orderedCategorySelections),
    );
    unselectedCategoryChoices.sort(unselectedChoicesSort);
    return selectedCategoryChoices.concat(unselectedCategoryChoices);
  }

  getCategoryProps() {
    const selections = this.state.data || [];
    return this.getOrderedChoices().map(([categoryOption, childOptions]) => ({
      id: categoryOption[0],
      categoryOption,
      childOptions,
      selections,
      onSelectChild: this.onSelectChild.bind(this),
      onDeselectChild: this.onDeselectChild.bind(this),
      onSelectCategory: this.onSelectCategory.bind(this),
      onDeselectCategory: this.onDeselectCategory.bind(this),
    }));
  }

  render() {
    return (
      <div className={'FormOrderedSelectMultiple'}>
        <DraggableList
          container={() => document.body}
          itemKey={'id'}
          list={this.getCategoryProps()}
          onMoveEnd={(items) => this.onReorder(items)}
          template={Category}
        />
      </div>
    );
  }

  onReorder(orderedDragItems) {
    const selections = cloneDeep(this.state.data || []);
    const orderedCategories = orderedDragItems.map((item) => item.id);
    selections.sort(getSelectionsSort(orderedCategories));
    this.setState(
      {
        data: selections,
        prevData: this.state.data,
      },
      () => {
        this.props.handleChange(selections);
      },
    );
  }

  onSelectCategory(category) {
    const selections = [...cloneDeep(this.state.data || []), [category, []]];
    this.props.handleChange(selections);
  }

  onDeselectCategory(category) {
    const selections = cloneDeep(this.state.data || []).filter(
      (cat) => cat[0] !== category,
    );
    this.props.handleChange(selections);
  }

  onSelectChild(category, child) {
    const selections = cloneDeep(this.state.data || []);

    for (const [categorySelection, childSelections] of selections) {
      if (categorySelection === category) {
        childSelections.push(child);
      }
    }

    this.props.handleChange(selections);
  }

  onDeselectChild(category, child) {
    const selections = cloneDeep(this.state.data || []);

    for (const [categorySelection, childSelections] of selections) {
      if (categorySelection === category) {
        const index = childSelections.indexOf(child);
        if (index > -1) {
          childSelections.splice(index, 1);
        }
      }
    }

    this.props.handleChange(selections);
  }
}
